import React from 'react';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DateField as RefineDataField } from '@refinedev/mui';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type Researcher } from '@aphp-ema/common/researchers';
import { TRANSLATION_KEYS } from '@/features/researchers/constants';
import { cellContentStyles } from '@/features/researchers/styles';
import { UserActions } from './UserActions';

/**
 * Component that provides column definitions for the user list data grid
 */
export const useUserListColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'email',
        headerName: t(TRANSLATION_KEYS.FIELDS.EMAIL),
        minWidth: 200,
        flex: 1,
        display: 'flex',
      },
      {
        field: 'firstName',
        headerName: t(TRANSLATION_KEYS.FIELDS.FIRST_NAME),
        minWidth: 150,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<Researcher, string | null>,
        ) {
          if (!params.value) return '-';
          return (
            <Typography component="p" sx={cellContentStyles}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: 'lastName',
        headerName: t(TRANSLATION_KEYS.FIELDS.LAST_NAME),
        minWidth: 150,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<Researcher, string | null>,
        ) {
          if (!params.value) return '-';
          return (
            <Typography component="p" sx={cellContentStyles}>
              {params.value}
            </Typography>
          );
        },
      },
      {
        field: 'role',
        headerName: t(TRANSLATION_KEYS.FIELDS.ROLES),
        minWidth: 150,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<Researcher, string | string[] | null>,
        ) {
          if (!params.value || params.value.length === 0) return '-';
          return (
            <Typography component="p" sx={cellContentStyles}>
              {Array.isArray(params.value)
                ? params.value
                    .map((role) =>
                      t(
                        //@ts-expect-error - TO FIX
                        `${TRANSLATION_KEYS.ROLES.PREFIX}${role.toLowerCase()}`,
                      ),
                    )
                    .join(', ')
                : t(
                    //@ts-expect-error - TO FIX
                    `${TRANSLATION_KEYS.ROLES.PREFIX}${params.value.toLowerCase()}`,
                  )}
            </Typography>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: t(TRANSLATION_KEYS.FIELDS.CREATED_AT),
        minWidth: 120,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<Researcher, string | Date | null>,
        ) {
          return <RefineDataField value={params.value} />;
        },
      },
      {
        field: 'actions',
        headerName: t(TRANSLATION_KEYS.TABLE.ACTIONS),
        align: 'right',
        headerAlign: 'right',
        minWidth: 80,
        sortable: false,
        display: 'flex',
        renderCell: function render(params: GridRenderCellParams<Researcher>) {
          return <UserActions recordItemId={params.row.id} />;
        },
      },
    ],
    [t],
  );
};
